import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isAdminPath } from '@functional';

import Backdrop from '@mui/material/Backdrop';
import ScaleLoader from 'react-spinners/ScaleLoader';

import PrivatePageWrapperUser from '~/components/layouts/User/PrivatePageWrapper';
import PrivatePageWrapperAdmin from '~/components/layouts/Admin/PrivatePageWrapper';
import PublicPageWrapperUser from '~/components/layouts/User/PublicPageWrapper';
import PublicPageWrapperAdmin from '~/components/layouts/Admin/PublicPageWrapper';
import GmoHeader from '~/components/layouts/Common/GmoHeader';
import GmoFooter from '~/components/layouts/Common/GmoFooter';

import { ROUTES } from '~/constants/routes';
import { HTTP_STATUS } from '~/constants/api';

import { PALETTE, Z_INDEX } from '~/assets/styles/base';
import { SESSION_STORAGES } from '~/constants';

// If adminLayout is true
//   the admin page does not include the sub-header Inquiry area, so padding is 96px 0.
// Else, padding top is 146px.
const Inner = styled.div<{ $wideLayout?: boolean; $adminLayout?: boolean }>`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${({ $adminLayout }) => ($adminLayout ? '96px 0' : '146px 0px 78px 0px')};
  ${({ $wideLayout }) =>
    $wideLayout &&
    css`
      max-width: 100%;
      margin: 0;
      padding: 96px 44px;
    `}
`;

const LoadingWrapper = styled.div`
  position: relative;
`;

const LoadingMessage = styled.div`
  width: max-content;
  position: absolute;
  bottom: -32px;
  left: 50%;
  transform: translate(-50%, 0);
`;

interface PageWrapperProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  wideLayout?: boolean;
}

const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  requireAuth = false,
  wideLayout = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // selector
  const axiosError = useSelector((state: any) => state.axiosError);
  const apiRequestState = useSelector((state: any) => state.apiRequest);

  // local state
  // エラーが出たらそれ以降の無駄なAPIコールを防ぐために、ローカルステートでエラー状態を管理する。
  const [hasError, setHasError] = useState<boolean | null>(null);

  // locationチェンジでスクロール位置をページ上部に移動させる
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (axiosError === null) {
      setHasError(false);
      return;
    }
    setHasError(true);

    /**
     * ↓↓↓ APIエラーがある場合 ↓↓↓
     * 共通エラーハンドリング
     */
    const { status } = axiosError;

    // axiosのerror.statusがundefinedの場合は、500エラーとみなす（stateの初期値はnull）
    if (status === undefined && isAdminPath()) {
      navigate(ROUTES.ADMIN[500]);
      return;
    }
    if (status === undefined && !isAdminPath()) {
      navigate(ROUTES.USER[500]);
      return;
    }

    console.error(
      'This message is thrown by gmoask.\nsome errors are raised on requesting to API.\nstatus: ',
      status
    );

    // エラーがあればエラー画面へ遷移
    if (status) {
      switch (status) {
        /**
         * 401のときは以下を行う。
         *   - localStorageをclear。
         *   - sessionStorageに表示しようとしていた画面のURLをセット（ログイン後にその画面にredirectさせるため）。
         */
        case HTTP_STATUS.UNAUTHORIZED:
          localStorage.clear();
          if (isAdminPath()) {
            // 401のときはuserSliceを初期化
            dispatch({ type: 'admin/unauthorizedErrorRaised' });
            // 401の後、ログインページで再度401が起きた時にログインページがセットされるのを防止。
            if (window.location.pathname !== ROUTES.ADMIN.SIGNIN) {
              sessionStorage.setItem(
                SESSION_STORAGES.ADMIN_UNAUTHORIZED_ERROR_REFERRER,
                window.location.pathname
              );
            }
            navigate(ROUTES.ADMIN.SIGNIN);
            break;
          }
          // 401のときはuserSliceを初期化
          dispatch({ type: 'user/unauthorizedErrorRaised' });
          // 401の後、ログインページで再度401が起きた時にログインページがセットされるのを防止。
          if (window.location.pathname !== ROUTES.USER.SIGNIN) {
            sessionStorage.setItem(
              SESSION_STORAGES.USER_UNAUTHORIZED_ERROR_REFERRER,
              window.location.pathname
            );
          }
          navigate(ROUTES.USER.SIGNIN);
          break;

        case HTTP_STATUS.FORBIDDEN:
          if (isAdminPath()) {
            navigate(ROUTES.ADMIN[403]);
            break;
          }
          navigate(ROUTES.USER[403]);
          break;

        case HTTP_STATUS.NOT_FOUND:
          if (isAdminPath()) {
            navigate(ROUTES.ADMIN[404]);
            break;
          }
          navigate(ROUTES.USER[404]);
          break;

        case HTTP_STATUS.BAD_REQUEST:
        case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        case HTTP_STATUS.SERVICE_UNAVAILABLE:
        case HTTP_STATUS.GATEWAY_TIMEOUT:
        default:
          if (isAdminPath()) {
            navigate(ROUTES.ADMIN[500]);
            break;
          }
          navigate(ROUTES.USER[500]);
          break;
      }
    }

    /**
     * ↑↑↑ APIエラーがある場合 ↑↑↑
     */
  }, [axiosError]);

  /**
   * APIエラーがある場合はredirectするので意味がないが、
   * TSエラーを防ぐため、nullを明示的に返却する。
   */
  if (hasError) {
    return null;
  }

  /**
   * ↓↓↓ APIエラーがない場合 ↓↓↓
   * axiosのerror.responseがnull（初期値）の場合は、そのままページ表示。
   * 直接axiosErrorSliceを参照すると、エラーページへのnavigate直後に、ここで新たなrender処理が走り、無駄にAPIが呼ばれるため、
   * ローカルステートのhasErrorを見てrenderするかどうかを判定する。
   *
   * URLパスによってWrapperを切り替える。
   */
  let wrapper: React.ReactNode;
  if (requireAuth && !isAdminPath()) {
    wrapper = (
      <PrivatePageWrapperUser>
        <Backdrop
          sx={{
            color: PALETTE.white,
            zIndex: Z_INDEX.max,
          }}
          open={apiRequestState.wholeLoading}
        >
          <LoadingWrapper>
            <ScaleLoader color={PALETTE.white} />
            <LoadingMessage>{apiRequestState.wholeLoadingMessage}</LoadingMessage>
          </LoadingWrapper>
        </Backdrop>
        <Inner $adminLayout={isAdminPath()}>{children}</Inner>
      </PrivatePageWrapperUser>
    );
  } else if (requireAuth && isAdminPath()) {
    wrapper = (
      <PrivatePageWrapperAdmin>
        <Backdrop
          sx={{
            color: PALETTE.white,
            zIndex: Z_INDEX.max,
          }}
          open={apiRequestState.wholeLoading}
        >
          <LoadingWrapper>
            <ScaleLoader color={PALETTE.white} />
            <LoadingMessage>{apiRequestState.wholeLoadingMessage}</LoadingMessage>
          </LoadingWrapper>
        </Backdrop>
        <Inner
          data-testid="PrivatePageWrapperAdminInnerLayout"
          $wideLayout={wideLayout}
          $adminLayout={isAdminPath()}
        >
          {children}
        </Inner>
      </PrivatePageWrapperAdmin>
    );
  } else if (isAdminPath()) {
    wrapper = (
      <PublicPageWrapperAdmin>
        <Backdrop
          sx={{ color: PALETTE.white, zIndex: Z_INDEX.max }}
          open={apiRequestState.wholeLoading}
        >
          <ScaleLoader color={PALETTE.white} />
        </Backdrop>
        <Inner>{children}</Inner>
      </PublicPageWrapperAdmin>
    );
  } else {
    wrapper = (
      <PublicPageWrapperUser>
        <Backdrop
          sx={{ color: PALETTE.white, zIndex: Z_INDEX.max }}
          open={apiRequestState.wholeLoading}
        >
          <ScaleLoader color={PALETTE.white} />
        </Backdrop>
        <Inner>{children}</Inner>
      </PublicPageWrapperUser>
    );
  }

  return (
    <>
      <GmoHeader />
      {wrapper}
      <GmoFooter />
    </>
  );
};

PageWrapper.propTypes = {
  children: PropTypes.node,
  requireAuth: PropTypes.bool,
  wideLayout: PropTypes.bool,
};

export default PageWrapper;
